import { useState, useEffect, useMemo, useCallback } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight, ChevronDown, Clock,
  CheckCircle,
  Circle, SkipForward, Loader2,
  Lock,
  FileQuestion,
  BookOpen,
  Sparkles
} from "lucide-react"
import { useStartCourse, useCompleteBlock } from "../hooks/useCourseProgress"
import { useCompleteLesson } from "../hooks/useLessonProgress"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { courseHierarchyApiResponseSchema } from "../utils/schemas/course-hierarchy-schema"
import { lessonContentApiResponseSchema } from "../utils/schemas/lesson-content-schema"
import { Button } from "../components/ui/button"
import Card, { CardContent } from "../components/Card"
import VideoBlock from "../components/course/VideoBlock"
import TextBlock from "../components/course/TextBlock"
import AssignmentBlock from "../components/course/AssignmentBlock"
import ImageBlock from "../components/course/ImageBlock"
import DownloadBlock from "../components/course/DownloadBlock"
import ModuleLockModal from "../components/ModuleLockModal"
import { showErrorToast, showSuccessToast } from "../utils/toast"
import { formatDuration } from "../utils/formatters"
import { useTranslation } from "../i18n"
import { CourseReaderSkeleton } from "../components/skeletons"

export default function CourseReaderPage() {
  const { courseId, moduleId, lessonId, blockId } = useParams()
  const navigate = useNavigate()
  const { tObj, t, language } = useTranslation()
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0)
  const [completedBlocks, setCompletedBlocks] = useState(new Set())
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [lockModalOpen, setLockModalOpen] = useState(false)
  const [_moduleAccessError, setModuleAccessError] = useState(null)
  const [expandedModules, setExpandedModules] = useState(new Set())

  // Hook pour démarrer un cours (si pas encore démarré)
  const { start: startCourse } = useStartCourse()
  
  // Hook pour compléter un bloc individuellement (si nécessaire)
  const { complete: completeBlock } = useCompleteBlock()
  
  // Hook pour compléter une chapitre entière
  const { complete: completeLesson } = useCompleteLesson()
  
  // Démarrer le cours automatiquement (l'API gère si le cours est déjà démarré)
  // La vérification d'accès se fait via AccessGuard
  useEffect(() => {
    if (courseId) {
      // Essayer de démarrer le cours - l'API retournera 400 si déjà démarré (on ignore)
      startCourse(courseId).catch(err => {
        // Si erreur 403 : utilisateur non enrôlé
        if (err?.status === 403) {
          console.error('Utilisateur non enrôlé au cours. Redirection...')
          // Rediriger vers la page du cours pour s'enrôler
          window.location.href = `/courses/${courseId}`
        } else if (err?.status === 400) {
          // Ignorer l'erreur si le cours est déjà démarré (400) - c'est normal
          console.log('Course already started')
        } else {
          console.error('Error starting course:', err)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  // Fetch course hierarchy using the new API
  // Always include language parameter (default to 'fr' if not set)
  const userLanguage = language || 'fr'
  const hierarchyEndpoint = courseId
    ? `${endpoints.courseHierarchy(courseId)}?language=${userLanguage}`
    : null

  const {
    data: hierarchyData,
    loading: hierarchyLoading,
    error: hierarchyError,
    execute: executeHierarchyFetch
  } = useGet(hierarchyEndpoint || `${endpoints.courseHierarchy('disabled')}`, {
    schema: courseHierarchyApiResponseSchema
  })

  const refetchHierarchy = useCallback(() => executeHierarchyFetch(undefined, { force: true }), [executeHierarchyFetch])

  const modules = useMemo(() => hierarchyData?.modules || [], [hierarchyData?.modules])
  const course = hierarchyData?.course

  // Get current module
  const currentModule = modules.find(module => module.id === moduleId)

  // Determine which lesson to display
  // If lessonId is in URL, use it; otherwise use first lesson of current module
  const targetLessonId = lessonId || currentModule?.lessons?.[0]?.id || null

  // Fetch lesson content using the new API (includes access check)
  // Always include language parameter to get translated content
  const lessonContentEndpoint = targetLessonId
    ? `${endpoints.lessonContent(targetLessonId)}?language=${userLanguage}`
    : null

  const {
    data: lessonContentData,
    loading: lessonContentLoading,
    error: lessonContentError,
    execute: executeLessonContentFetch
  } = useGet(lessonContentEndpoint || `${endpoints.lessonContent('disabled')}`, {
    schema: lessonContentApiResponseSchema,
    immediate: Boolean(targetLessonId)
  })

  const refetchLessonContent = useCallback(() => executeLessonContentFetch(undefined, { force: true }), [executeLessonContentFetch])

  // Extract lesson data
  const lesson = lessonContentData
  const blocks = useMemo(() => lesson?.blocks || [], [lesson?.blocks])
  const accessInfo = lesson?.access
  const hasAccess = accessInfo?.hasAccess ?? true
  const accessReason = accessInfo?.reason
  const accessCode = accessInfo?.code
  const accessDetails = accessInfo?.details || {}
  const moduleQuiz = lesson?.module?.quiz

  // Get quiz from lesson content (module quiz)
  const quiz = moduleQuiz

  // Handle access errors from lesson content API
  useEffect(() => {
    if (!lessonContentLoading && !hasAccess && accessCode && targetLessonId) {
      const errorMessage = accessReason || tObj.courseReader.mustValidatePreviousQuiz()
      
      setModuleAccessError(errorMessage)
      setLockModalOpen(true)
      showErrorToast({ message: errorMessage })
    }
  }, [lessonContentLoading, hasAccess, accessCode, accessReason, targetLessonId, tObj])
  
  // Handle 403 errors (fallback)
  useEffect(() => {
    if (lessonContentError?.status === 403 || hierarchyError?.status === 403) {
      const errorMessage = lessonContentError?.response?.data?.message || hierarchyError?.response?.data?.message || 
                          tObj.courseReader.mustValidatePreviousQuiz()
      
      setModuleAccessError(errorMessage)
      setLockModalOpen(true)
      showErrorToast({ message: errorMessage })
    }
  }, [lessonContentError, hierarchyError, tObj, setModuleAccessError])

  // Listen for quiz passed event to refresh hierarchy and lesson content
  useEffect(() => {
    const handleQuizPassed = (event) => {
      const { moduleId: passedModuleId, courseId: passedCourseId } = event.detail
      
      // If the passed quiz is in the current course, refresh hierarchy and lesson content
      if (passedCourseId === courseId) {
        refetchHierarchy()
        if (targetLessonId) {
          refetchLessonContent()
        }
        
        // If the next module is now accessible, show a notification
        if (passedModuleId && modules.length > 0) {
          const passedModuleIndex = modules.findIndex(m => m.id === passedModuleId)
          if (passedModuleIndex >= 0 && passedModuleIndex < modules.length - 1) {
            const nextModule = modules[passedModuleIndex + 1]
            if (nextModule) {
              showSuccessToast({ 
                message: t('courseReader.moduleUnlocked', { title: nextModule.title })
              })
            }
          }
        }
      }
    }

    window.addEventListener('quizPassed', handleQuizPassed)
    return () => {
      window.removeEventListener('quizPassed', handleQuizPassed)
    }
  }, [courseId, moduleId, targetLessonId, modules, refetchHierarchy, refetchLessonContent, t])

  // Initialize completed blocks from lesson content
  // Note: lesson content API doesn't include progress data
  // We'll track completion locally

  const loading = hierarchyLoading || lessonContentLoading

  // Find current module and navigation info
  const currentModuleIndex = modules.findIndex(module => module.id === moduleId)
  const isFirstModule = currentModuleIndex === 0
  const isLastModule = currentModuleIndex === modules.length - 1
  const previousModule = !isFirstModule ? modules[currentModuleIndex - 1] : null
  const nextModule = !isLastModule ? modules[currentModuleIndex + 1] : null

  // Get current lesson info
  const currentLessonIndex = currentModule?.lessons?.findIndex(l => l.id === targetLessonId) ?? -1
  const isFirstLesson = currentLessonIndex === 0
  const isLastLesson = currentLessonIndex === (currentModule?.lessons?.length ?? 0) - 1
  const previousLesson = !isFirstLesson && currentModule?.lessons ? currentModule.lessons[currentLessonIndex - 1] : null
  const nextLesson = !isLastLesson && currentModule?.lessons ? currentModule.lessons[currentLessonIndex + 1] : null
  
  // Get current lesson from hierarchy
  const currentLesson = currentModule?.lessons?.find(l => l.id === targetLessonId)
  const isCurrentLessonCompleted = currentLesson?.isCompleted === true

  // Get quiz progress data from hierarchy
  const isQuizPassed = moduleQuiz?.isPassed || false
  
  // Check if all previous modules are completed
  const allPreviousModulesCompleted = useMemo(() => {
    if (currentModuleIndex === 0) return true
    
    for (let i = 0; i < currentModuleIndex; i++) {
      const prevModule = modules[i]
      if (!prevModule) continue
      
      const hasQuiz = prevModule.quiz !== null
      const isQuizPassed = prevModule.quiz?.isPassed === true
      
      if (hasQuiz && !isQuizPassed) {
        return false
      }
    }
    
    return true
  }, [modules, currentModuleIndex])
  
  // Check if all blocks of current lesson are completed (simplified - we'll track this locally)
  const allBlocksCompleted = completedBlocks.size === blocks.length && blocks.length > 0
  
  // Check if all lessons of the current module are completed
  const allLessonsCompleted = useMemo(() => {
    if (!currentModule?.lessons || currentModule.lessons.length === 0) {
      return true // No lessons means nothing to complete
    }
    
    // Check if all lessons are completed
    return currentModule.lessons.every(lesson => lesson.isCompleted === true)
  }, [currentModule?.lessons])
  
  // Quiz can be accessed if:
  // 1. All previous modules are completed (their quizzes are passed)
  // 2. All lessons of the current module are completed
  // 3. OR the quiz is already passed
  const canAccessQuiz = (allLessonsCompleted || isQuizPassed) && allPreviousModulesCompleted

  // Si blockId est présent dans l'URL, trouver l'index du bloc correspondant
  useEffect(() => {
    if (blockId && blocks && blocks.length > 0) {
      const blockIndex = blocks.findIndex(block => block.id === blockId)
      if (blockIndex !== -1 && blockIndex !== currentBlockIndex) {
        setCurrentBlockIndex(blockIndex)
      }
    }
  }, [blockId, blocks, currentBlockIndex])

  // Get current block
  const currentBlock = blocks[currentBlockIndex]
  const isFirstBlock = currentBlockIndex === 0
  const isLastBlock = currentBlockIndex === blocks.length - 1

  // Vérifier si le bloc actuel est concaténé (TEXT, IMAGE, VIDEO)
  const concatenableTypes = ['TEXT', 'IMAGE', 'VIDEO']
  const isCurrentBlockConcatenated = currentBlock && concatenableTypes.includes(currentBlock.type)
  
  // Note: hasOtherBlocksAfterConcatenated supprimé car non utilisé

  // Navigation functions (obsolete - now navigating between lessons)
  const _goToPreviousBlock = async () => {
    if (!isFirstBlock && !isNavigating) {
      setIsNavigating(true)
      try {
        // Si on est dans un bloc concaténé, aller au module précédent
        if (isCurrentBlockConcatenated) {
          if (previousModule) {
            navigate(`/courses/${courseId}/modules/${previousModule.id}`)
          }
        } else {
          // Navigation normale entre blocs
          const prevIndex = currentBlockIndex - 1
          const prevBlock = blocks[prevIndex]
          if (prevBlock) {
            // Si le bloc précédent est concaténé, aller au module précédent
            if (concatenableTypes.includes(prevBlock.type)) {
              if (previousModule) {
                navigate(`/courses/${courseId}/modules/${previousModule.id}`)
              }
            } else {
              if (targetLessonId) {
              navigate(`/courses/${courseId}/modules/${moduleId}/lessons/${targetLessonId}/blocks/${prevBlock.id}`, { replace: true })
            }
              setCurrentBlockIndex(prevIndex)
            }
          }
        }
      } finally {
        setTimeout(() => setIsNavigating(false), 300)
      }
    }
  }

  const _goToNextBlock = async () => {
    if (isNavigating) return
  
    setIsNavigating(true)
    try {
      // Marquer le bloc actuel comme terminé avant de passer au suivant
      if (currentBlock) {
        await markBlockAsCompleted()
      }
      
      // Si on est dans un bloc concaténé, passer directement au module suivant (ou quiz)
      if (isCurrentBlockConcatenated) {
        // Vérifier s'il y a un quiz
        if (quiz) {
          // Vérifier que tous les modules précédents sont complétés
          if (!allPreviousModulesCompleted) {
            const incompleteModule = modules.find((m, idx) => {
              if (idx >= currentModuleIndex) return false
              const hasQuiz = m.quiz !== null
              const isQuizPassed = m.quiz?.isPassed === true
              return hasQuiz && !isQuizPassed
            })
          }
          return
        }
  
        // Aller au quiz ou au module suivant
        if (quiz) {
          navigate(`/courses/${courseId}/modules/${moduleId}/quiz`)
        } else if (nextModule) {
          navigate(`/courses/${courseId}/modules/${nextModule.id}`)
        }
        return
      }
  
      // === Sinon : bloc normal (ASSIGNMENT, DOWNLOAD, etc.) ===
      if (currentBlock) {
        await markBlockAsCompleted()
      }
  
      // Navigation classique entre blocs non concaténés
      if (!isLastBlock) {
        const nextIndex = currentBlockIndex + 1
        const nextBlock = blocks[nextIndex]
        if (nextBlock) {
          if (concatenableTypes.includes(nextBlock.type)) {
            // Prochain bloc est concaténé → on passe directement au quiz/module suivant
            if (quiz) {
              if (!allPreviousModulesCompleted) {
                const incompleteModule = modules.find((m, idx) => {
                  if (idx >= currentModuleIndex) return false
              const hasQuiz = m.quiz !== null
              const isQuizPassed = m.quiz?.isPassed === true
              return hasQuiz && !isQuizPassed
                })
                if (incompleteModule) {
                  showErrorToast({ 
                    message: t('courseReader.mustValidateModuleQuiz', { title: incompleteModule.title })
                  })
                } else {
                  showErrorToast({ 
                    message: tObj.courseReader.mustCompletePreviousModules()
                  })
                }
                return
              }
              navigate(`/courses/${courseId}/modules/${moduleId}/quiz`)
            } else if (nextModule) {
              navigate(`/courses/${courseId}/modules/${nextModule.id}`)
            }
          } else {
            if (targetLessonId) {
              navigate(`/courses/${courseId}/modules/${moduleId}/lessons/${targetLessonId}/blocks/${nextBlock.id}`, { replace: true })
            }
            setCurrentBlockIndex(nextIndex)
          }
        }
      } else {
        // If we're at the last block of the lesson, complete the lesson first
        if (isLastBlock && targetLessonId && !allLessonsCompleted) {
          try {
            await completeCurrentLesson()
          } catch (err) {
            console.error('Error completing lesson:', err)
          }
        }
        
        // If we're at the last block of the module, check if there's a quiz
        if (quiz && isLastBlock) {
          // Vérifier que tous les modules précédents sont complétés avant d'accéder au quiz
          if (!allPreviousModulesCompleted) {
            const incompleteModule = modules.find((m, idx) => {
              if (idx >= currentModuleIndex) return false
              const hasQuiz = m.quiz !== null
              const isQuizPassed = m.quiz?.isPassed === true
              return hasQuiz && !isQuizPassed
            })
            if (incompleteModule) {
              showErrorToast({ 
                message: t('courseReader.mustValidateModuleQuiz', { title: incompleteModule.title })
              })
            } else {
              showErrorToast({ 
                message: tObj.courseReader.mustCompletePreviousModules()
              })
            }
            return
          }
          
          // Vérifier que toutes les chapitres du module sont complétées
          if (!allLessonsCompleted) {
            showErrorToast({ 
              message: tObj.courseReader.completeAllLessons?.() || 'Vous devez compléter toutes les chapitres du module avant de passer le quiz'
            })
            return
          }
          
          // Navigate to quiz - le backend vérifiera si toutes les chapitres sont complétées
          // Si ce n'est pas le cas, il retournera une erreur 403 que nous gérons déjà
          navigate(`/courses/${courseId}/modules/${moduleId}/quiz`)
        } else if (nextModule) {
          navigate(`/courses/${courseId}/modules/${nextModule.id}`)
        }
      }
    } finally {
      setTimeout(() => setIsNavigating(false), 300)
    }
  }

  const _goToPreviousModule = () => {
    if (previousModule && !isNavigating) {
      setIsNavigating(true)
      navigate(`/courses/${courseId}/modules/${previousModule.id}`)
      setTimeout(() => setIsNavigating(false), 300)
    }
  }

  const _goToNextModule = () => {
    if (nextModule && !isNavigating) {
      // Check if current module is completed before going to next
      if (allBlocksCompleted || isQuizPassed) {
        setIsNavigating(true)
        // Navigate to first lesson of next module
        const firstLesson = nextModule.lessons?.[0]
        if (firstLesson) {
          navigate(`/courses/${courseId}/modules/${nextModule.id}/lessons/${firstLesson.id}`)
        } else {
          navigate(`/courses/${courseId}/modules/${nextModule.id}`)
        }
        setTimeout(() => setIsNavigating(false), 300)
      }
    }
  }

  // Check if a module is accessible (simplified - based on hierarchy data)
  const isModuleAccessible = (module) => {
    if (!module) return false
    const moduleIndex = modules.findIndex(m => m.id === module.id)
    if (moduleIndex === 0) return true // First module is always accessible
    
    // Check if all previous modules have completed their quizzes
    for (let i = 0; i < moduleIndex; i++) {
      const prevModule = modules[i]
      if (!prevModule) continue
      const hasQuiz = prevModule.quiz !== null
      const isQuizPassed = prevModule.quiz?.isPassed === true
      if (hasQuiz && !isQuizPassed) {
        return false
      }
    }
    return true
  }


  // Toggle module expansion in sidebar
  const toggleModule = useCallback((moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }, [])

  // Auto-expand current module to show lessons
  useEffect(() => {
    if (moduleId && !expandedModules.has(moduleId)) {
      setExpandedModules(prev => new Set([...prev, moduleId]))
    }
  }, [moduleId, expandedModules])

  // Navigate to a specific block within the current lesson
  const goToBlock = async (blockIndex) => {
    const block = blocks[blockIndex]
    if (block && targetLessonId) {
      // Update URL with blockId
      navigate(`/courses/${courseId}/modules/${moduleId}/lessons/${targetLessonId}/blocks/${block.id}`, { replace: true })
    }
    setCurrentBlockIndex(blockIndex)
    setSidebarOpen(false)
  }

  // Navigate to previous lesson
  const goToPreviousLesson = async () => {
    if (previousLesson && !isNavigating) {
      setIsNavigating(true)
      try {
        navigate(`/courses/${courseId}/modules/${moduleId}/lessons/${previousLesson.id}`)
      } finally {
        setTimeout(() => setIsNavigating(false), 300)
      }
    } else if (previousModule && !isNavigating) {
      // If no previous lesson, go to last lesson of previous module
      setIsNavigating(true)
      try {
        const lastLesson = previousModule.lessons?.[previousModule.lessons.length - 1]
        if (lastLesson) {
          navigate(`/courses/${courseId}/modules/${previousModule.id}/lessons/${lastLesson.id}`)
        } else {
          navigate(`/courses/${courseId}/modules/${previousModule.id}`)
        }
      } finally {
        setTimeout(() => setIsNavigating(false), 300)
      }
    }
  }

  // Navigate to next lesson (or quiz if last lesson)
  const goToNextLesson = async () => {
    if (isNavigating) return
    
    setIsNavigating(true)
    try {
      // If current lesson is not completed, complete it first
      if (!isCurrentLessonCompleted && targetLessonId) {
        try {
          await completeCurrentLesson()
          // Refresh hierarchy to get updated lesson status
          await refetchHierarchy()
        } catch (err) {
          console.error('Error completing lesson:', err)
          showErrorToast({ message: 'Erreur lors de la validation du chapitre. Veuillez réessayer.' })
          setIsNavigating(false)
          return
        }
      }
      
      // If last lesson of the module, go to quiz (if available) to validate the module
      if (isLastLesson) {
        if (moduleQuiz) {
          // Always go to quiz if available (the API will verify access)
          navigate(`/courses/${courseId}/modules/${moduleId}/quiz`)
        } else if (nextModule) {
          // No quiz, go to first lesson of next module
          const firstLesson = nextModule.lessons?.[0]
          if (firstLesson) {
            navigate(`/courses/${courseId}/modules/${nextModule.id}/lessons/${firstLesson.id}`)
          } else {
            navigate(`/courses/${courseId}/modules/${nextModule.id}`)
          }
        }
      } else if (nextLesson) {
        // Go to next lesson in current module (le chapitre actuelle a déjà été validée si nécessaire)
        navigate(`/courses/${courseId}/modules/${moduleId}/lessons/${nextLesson.id}`)
      }
    } finally {
      setTimeout(() => setIsNavigating(false), 300)
    }
  }

  // Complete the entire lesson (marks all blocks as completed)
  const completeCurrentLesson = async () => {
    if (!targetLessonId) return
    
    try {
      await completeLesson(targetLessonId)
      // Refresh hierarchy and lesson content to get updated progress
      await refetchHierarchy()
      await refetchLessonContent()
      // Mark all blocks as completed locally
      const allBlockIndices = blocks.map((_, index) => index)
      setCompletedBlocks(new Set(allBlockIndices))
      showSuccessToast({ message: tObj.courseReader.lessonCompleted?.() || 'Chapitre complétée avec succès' })
    } catch (err) {
      console.error('Error completing lesson:', err)
    }
  }

  // Mark a single block as completed (for individual block completion)
  const markBlockAsCompleted = async () => {
    if (!currentBlock) return
    
    try {
      // Mark block as completed in API
      await completeBlock(currentBlock.id, {
        // Add score and timeSpent if available
        // score: calculatedScore,
        // timeSpent: timeSpentInSeconds
      })
      // Update local state
      setCompletedBlocks(prev => new Set([...prev, currentBlockIndex]))
    } catch (err) {
      console.error('Error completing block:', err)
      // Update local state anyway in case of error
      setCompletedBlocks(prev => new Set([...prev, currentBlockIndex]))
    }
  }
  
  // La progression des blocs est maintenant gérée au niveau de le chapitre
  // Plus besoin de démarrer individuellement chaque bloc


  // Get block icon based on type
  const getBlockIcon = (type) => {
    switch (type) {
      case 'VIDEO':
        return '🎥'
      case 'TEXT':
        return '📖'
      case 'ASSIGNMENT':
        return '✏️'
      case 'IMAGE':
        return '🖼️'
      case 'DOWNLOAD':
        return '📁'
      default:
        return '📄'
    }
  }

  // Render a single block content based on type (without title, as it's shown in header)
  const renderSingleBlock = (block, isConcatenated = false) => {
    if (!block) return null

    // Check if block has content
    if (!block.content) {
      return (
        <div className="p-6 text-center bg-gray-50 rounded-lg">
          <p className="text-gray-500">{tObj.courseReader.contentNotAvailable()}</p>
          <p className="text-sm text-gray-400 mt-2">{tObj.courseReader.loading()}</p>
        </div>
      )
    }

    switch (block.type) {
      case 'VIDEO':
        return <VideoBlock block={block} hideTitle={true} isConcatenated={isConcatenated} />
      case 'TEXT':
        return <TextBlock block={block} hideTitle={true} isConcatenated={isConcatenated} />
      case 'ASSIGNMENT':
        return <AssignmentBlock block={block} />
      case 'IMAGE':
        return <ImageBlock block={block} hideTitle={true} isConcatenated={isConcatenated} />
      case 'DOWNLOAD':
        return <DownloadBlock block={block} hideTitle={true} />
      default:
        return (
          <div className="p-6 text-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">{t('courseReader.unsupportedContentType', { type: block.type })}</p>
          </div>
        )
    }
  }

  // Render all blocks concatenated together
  const renderAllBlocks = () => {
    if (!blocks || blocks.length === 0) {
      return (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-500">{tObj.courseReader.noContentAvailable()}</p>
          </CardContent>
        </Card>
      )
    }

    // Séparer les blocs concaténables (TEXT, IMAGE, VIDEO) des autres (ASSIGNMENT, DOWNLOAD)
    const concatenableTypes = ['TEXT', 'IMAGE', 'VIDEO']
    const concatenatedBlocks = []
    const otherBlocks = []

    blocks.forEach((block, index) => {
      if (concatenableTypes.includes(block.type)) {
        concatenatedBlocks.push({ block, originalIndex: index })
      } else {
        otherBlocks.push({ block, originalIndex: index })
      }
    })

    return (
      <div className="space-y-6">
        {/* CSS to impose app fonts on all content */}
        <style>{`
          .prose {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
          }
          .prose h1,
          .prose h2,
          .prose h3,
          .prose h4,
          .prose h5,
          .prose h6 {
            font-family: 'Fraunces', serif;
            font-weight: 400;
            line-height: 1.2;
          }
          .prose p {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
          }
          .prose ul,
          .prose ol {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
          }
          .prose li {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
          }
          .prose blockquote {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
          }
          .prose strong,
          .prose b {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            font-weight: 600;
          }
        `}</style>
        {/* Blocs concaténés (TEXT, IMAGE, VIDEO) - affichés comme un article fluide */}
        {concatenatedBlocks.length > 0 && (
          <article className="prose prose-lg max-w-none">
            {concatenatedBlocks.map(({ block, originalIndex }) => {
              const blockId = `block-${block.id}`
              const isFirst = originalIndex === 0
              
              return (
                <section
                  key={block.id}
                  id={blockId}
                  className={`relative ${isFirst ? 'mt-0' : 'mt-12'} ${
                    originalIndex === currentBlockIndex ? 'ring-2 ring-[#141835] ring-offset-2 rounded-lg p-2 -m-2' : ''
                  }`}
                >
                  {/* Titre du bloc (seulement si présent) */}
                  {block.title && (
                    <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-0">
                      {block.title}
                    </h2>
                  )}
                  
                  {/* Description si présente */}
                  {block.description && (
                    <p className="text-gray-600 text-lg mb-6 leading-relaxed">{block.description}</p>
                  )}

                  {/* Contenu du bloc - sans Card wrapper pour un rendu plus fluide */}
                  <div className="block-content-wrapper">
                    {renderSingleBlock(block, true)}
                  </div>
                </section>
              )
            })}
          </article>
        )}

        {/* Autres blocs (ASSIGNMENT, DOWNLOAD) - affichés avec leur structure complète */}
        {otherBlocks.map(({ block, originalIndex }) => {
          const isCompleted = completedBlocks.has(originalIndex) || block.isCompleted || block.progress?.status === 'COMPLETED'
          const blockId = `block-${block.id}`
          
          return (
            <div
              key={block.id}
              id={blockId}
              className={`relative transition-all duration-300 ${
                originalIndex === currentBlockIndex ? 'ring-2 ring-[#141835] ring-offset-4 rounded-xl p-2 -m-2' : ''
              }`}
            >
              {/* Block header with title and progress indicator */}
              <div className="mb-6 flex items-start gap-4 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-3xl">{getBlockIcon(block.type)}</span>
                  {isCompleted && (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-gray-900">{block.title}</h3>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                      {originalIndex + 1}/{blocks.length}
                    </span>
                  </div>
                  {block.description && (
                    <p className="text-base text-gray-600 leading-relaxed">{block.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatDuration(block.duration, 'minutes')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Block content - avec titre pour ASSIGNMENT et DOWNLOAD */}
              <div className="block-content-wrapper">
                {renderSingleBlock(block)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Loading state
  if (loading || hierarchyLoading || lessonContentLoading) {
    return <CourseReaderSkeleton />
  }

  // Error state (except 403 which is handled by modal)
  if (lessonContentError && lessonContentError.status !== 403) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{tObj.courseReader.errorTitle()}</h2>
          <p className="text-gray-600 mb-4">{lessonContentError.message || tObj.courseReader.errorMessage()}</p>
          <Link to={`/courses/${courseId}`}>
            <Button>{tObj.courseReader.backToCourse()}</Button>
          </Link>
        </div>
      </div>
    )
  }

  // If lesson is not accessible, show message
  if (!lessonContentLoading && !hasAccess && accessCode && targetLessonId) {
    const previousLessonId = accessDetails?.previousLessonId
    const previousLessonTitle = accessDetails?.previousLessonTitle
    const previousModuleId = accessDetails?.previousModuleId
    
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center p-4 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-md bg-white border border-[#D5DAE3] p-8"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#203a8a] mb-6">
            <Lock className="w-8 h-8 text-[#E8C547]" />
          </div>
          <h2 className="font-display text-2xl lg:text-3xl text-[#203a8a] mb-3">
            {accessCode === 'PREVIOUS_LESSON_NOT_COMPLETED' 
              ? tObj.courseReader.lessonLocked?.() || 'Chapitre verrouillée'
              : accessCode === 'PREVIOUS_MODULE_NOT_COMPLETED'
              ? tObj.courseReader.moduleLocked()
              : tObj.courseReader.moduleLocked()}
          </h2>
          <p className="font-sans text-[#6B7280] mb-6">
            {accessReason || tObj.courseReader.moduleLockedDesc()}
          </p>
          {previousLessonId && previousLessonTitle && (
            <Button
              onClick={() => navigate(`/courses/${courseId}/modules/${accessDetails.moduleId}/lessons/${previousLessonId}`)}
              className="w-full bg-[#203a8a] hover:bg-[#E8C547] hover:text-[#203a8a] text-white text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors"
            >
              {tObj.courseReader.goToPreviousLesson?.() || `Aller au chapitre précédent: ${previousLessonTitle}`}
            </Button>
          )}
          {previousModuleId && !previousLessonId && (
            <Button
              onClick={() => navigate(`/courses/${courseId}/modules/${previousModuleId}`)}
              className="w-full bg-[#203a8a] hover:bg-[#E8C547] hover:text-[#203a8a] text-white text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors"
            >
              {tObj.courseReader.goToPreviousModule()}
            </Button>
          )}
          <Link to={`/courses/${courseId}`} className="block mt-3">
            <Button variant="outline" className="w-full border border-[#D5DAE3] text-[#6B7280] hover:border-[#203a8a] hover:text-[#203a8a] text-[10px] uppercase tracking-[0.22em] font-semibold">
              {tObj.courseReader.backToCourse()}
            </Button>
          </Link>
        </motion.div>
      </div>
    )
  }

  // No blocks state
  if (blocks.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{tObj.courseReader.noContentAvailable()}</h2>
          <p className="text-gray-600 mb-4">{tObj.courseReader.noContentDesc()}</p>
          <Link to={`/courses/${courseId}`}>
            <Button>{tObj.courseReader.backToCourse()}</Button>
          </Link>
        </div>
      </div>
    )
  }

  // Protection d'accès via la vérification des chapitres (vérifie cours → module → chapitre en cascade)
  // Plus besoin d'AccessGuard car la vérification des chapitres fait tout
  return (
    <div className="min-h-screen bg-[#FAFBFD] pt-20">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-5 lg:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Sidebar éditoriale */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-1"
          >
            <div className="sticky top-20 sm:top-24">
              <div className="bg-white border border-[#D5DAE3] flex flex-col max-h-[calc(100vh-6rem)]">
                {/* Course Info & Progress - NOT scrollable */}
                {course && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    className="p-5 sm:p-6 bg-[#203a8a] text-white relative overflow-hidden flex-shrink-0"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#E8C547] opacity-10 -translate-y-12 translate-x-12 rotate-45"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <BookOpen className="w-4 h-4 text-[#E8C547]" />
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#E8C547] font-semibold">Cours</span>
                      </div>
                      <h2 className="font-display text-base sm:text-lg md:text-xl mb-4 !text-white break-words leading-tight" style={{ color: 'white' }}>{course.title}</h2>
                      {/* Course Progress */}
                      {(() => {
                        const totalLessons = modules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0)
                        const completedLessons = modules.reduce((sum, m) => 
                          sum + (m.lessons?.filter(l => l.isCompleted).length || 0), 0
                        )
                        const progressPercentage = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0
                        return (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] uppercase tracking-[0.22em] text-white/70">Progression</span>
                              <span className="font-display text-lg text-[#E8C547]">{Math.round(progressPercentage)}%</span>
                            </div>
                            <div className="w-full bg-white/15 h-1 relative overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercentage}%` }}
                                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                className="bg-[#E8C547] h-1 absolute top-0 left-0"
                              />
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-2">
                              {completedLessons}/{totalLessons} chapitres
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  </motion.div>
                )}

                {/* Modules List - SCROLLABLE */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  <div className="p-3 sm:p-4 border-b border-[#D5DAE3]">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#203a8a] mb-3 px-1 truncate">{tObj.courseReader.courseModules()}</div>
                    <div className="space-y-1">
              {modules.map((module, moduleIndex) => {
                const isAccessible = isModuleAccessible(module)
                const isCurrent = module.id === moduleId
                const moduleProgress = module.progress
                const isCompleted = moduleProgress?.isCompleted || false
                const isExpanded = expandedModules.has(module.id)
                const hasLessons = module.lessons && module.lessons.length > 0
                
                return (
                  <div key={module.id} className="space-y-1">
                    {/* Module Header */}
                    <div className="flex items-center gap-1">
                      {hasLessons && (
                        <button
                          onClick={() => toggleModule(module.id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          aria-label={isExpanded ? 'Réduire' : 'Développer'}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-3 h-3 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-3 h-3 text-gray-500" />
                          )}
                        </button>
                      )}
                      {!hasLessons && <div className="w-5" />}
                      <button
                        onClick={() => {
                          if (isAccessible && !isNavigating) {
                            setIsNavigating(true)
                            navigate(`/courses/${courseId}/modules/${module.id}`)
                            setTimeout(() => setIsNavigating(false), 300)
                          }
                        }}
                        disabled={!isAccessible || isNavigating}
                        className={`flex-1 text-left p-2 sm:p-2.5 text-xs sm:text-sm transition-all ${
                          isCurrent
                            ? 'bg-[#203a8a] text-white border-l-2 border-[#E8C547]'
                            : isAccessible
                            ? 'hover:bg-[#FAFBFD] hover:border-l-2 hover:border-[#203a8a] text-[#1B2240] border-l-2 border-transparent'
                            : 'opacity-50 cursor-not-allowed text-[#9CA3AF] bg-[#FAFBFD] border-l-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {!isAccessible && <Lock className="w-3 h-3 flex-shrink-0" />}
                          {isCompleted && isAccessible && <CheckCircle className="w-3 h-3 text-[#E8C547] flex-shrink-0" />}
                          <span className="truncate font-semibold text-xs sm:text-sm" title={module.title}>
                            <span className="text-[10px] uppercase tracking-[0.22em] opacity-60">{String(moduleIndex + 1).padStart(2, '0')}</span>
                            <span className="ml-2">{module.title}</span>
                          </span>
                        </div>
                      </button>
                    </div>
                    
                    {/* Lessons List (nested under module) */}
                    <AnimatePresence initial={false}>
                    {hasLessons && isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="ml-5 space-y-1 border-l border-[#D5DAE3] pl-2 overflow-hidden"
                      >
                        {module.lessons.map((lesson, lessonIndex) => {
                          const isCurrentLesson = lesson.id === targetLessonId && isCurrent
                          const isLessonCompleted = lesson.isCompleted === true
                          
                          // Vérifier si le chapitre est accessible
                          const previousLesson = lessonIndex > 0 ? module.lessons[lessonIndex - 1] : null
                          const isPreviousLessonCompleted = previousLesson ? previousLesson.isCompleted === true : true
                          const isLessonAccessible = lessonIndex === 0 || isPreviousLessonCompleted || isCurrentLesson
                          
                          return (
                            <button
                              key={lesson.id}
                              onClick={() => {
                                if (!isNavigating && isLessonAccessible) {
                                  setIsNavigating(true)
                                  navigate(`/courses/${courseId}/modules/${module.id}/lessons/${lesson.id}`)
                                  setTimeout(() => setIsNavigating(false), 300)
                                } else if (!isLessonAccessible) {
                                  showErrorToast({ 
                                    message: previousLesson 
                                      ? `Vous devez compléter le chapitre "${previousLesson.title}" avant d'accéder à ce chapitre`
                                      : 'Vous devez compléter le chapitre précédente avant d\'accéder à ce chapitre'
                                  })
                                }
                              }}
                              disabled={isNavigating || !isLessonAccessible}
                              className={`w-full text-left p-2 text-xs sm:text-sm transition-all ${
                                isCurrentLesson
                                  ? 'bg-[#E8C547]/15 text-[#203a8a] border-l-2 border-[#E8C547] font-semibold'
                                  : isLessonCompleted
                                  ? 'hover:bg-[#FAFBFD] text-[#6B7280] border-l-2 border-transparent'
                                  : isLessonAccessible
                                  ? 'hover:bg-[#FAFBFD] text-[#6B7280] border-l-2 border-transparent'
                                  : 'opacity-50 cursor-not-allowed text-[#9CA3AF] border-l-2 border-transparent'
                              }`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {!isLessonAccessible && !isCurrentLesson && <Lock className="w-3 h-3 text-[#9CA3AF] flex-shrink-0" />}
                                {isLessonCompleted && isLessonAccessible && <CheckCircle className="w-3 h-3 text-[#E8C547] flex-shrink-0" />}
                                {!isLessonCompleted && isCurrentLesson && <Circle className="w-3 h-3 text-[#203a8a] flex-shrink-0" />}
                                {!isLessonCompleted && !isCurrentLesson && isLessonAccessible && <Circle className="w-3 h-3 text-[#9CA3AF] flex-shrink-0" />}
                                <span className="truncate text-xs sm:text-sm" title={lesson.title}>
                                  <span className="text-[9px] uppercase tracking-[0.22em] opacity-60 mr-1.5">{String(lessonIndex + 1).padStart(2, '0')}</span>
                                  {lesson.title}
                                </span>
                              </div>
                            </button>
                          )
                        })}
                      </motion.div>
                    )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

          {/* Quiz Progress éditorial */}
          {quiz && (
            <div className="p-4 mt-auto border-t border-[#D5DAE3] bg-[#FAFBFD]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#203a8a]">{tObj.courseReader.quiz()}</span>
                  {quiz ? (
                    isQuizPassed ? (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#E8C547] font-semibold">{tObj.courseReader.passedQuiz()}</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280]">{tObj.courseReader.notAttempted()}</span>
                    )
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280]">{tObj.courseReader.available()}</span>
                  )}
                </div>
                <Button
                  onClick={async () => {
                    if (!canAccessQuiz) {
                      let errorMessage = tObj.courseReader.mustCompleteAllBlocks()
                      if (!allPreviousModulesCompleted) {
                        // Trouver le premier module précédent non complété
                        const incompleteModule = modules.find((m, idx) => {
                          if (idx >= currentModuleIndex) return false
                          const hasQuiz = m.quiz !== null
                          const isQuizPassed = m.quiz?.isPassed === true
                          return hasQuiz && !isQuizPassed
                        })
                        if (incompleteModule) {
                          errorMessage = t('courseReader.mustValidateModuleQuiz', { title: incompleteModule.title })
                        } else {
                          errorMessage = tObj.courseReader.mustCompletePreviousModules()
                        }
                      } else if (!allLessonsCompleted) {
                        errorMessage = tObj.courseReader.completeAllLessons?.() || 'Vous devez compléter toutes les chapitres du module'
                      } else if (!allBlocksCompleted) {
                        errorMessage = tObj.courseReader.mustCompleteAllBlocks()
                      }
                      showErrorToast({ message: errorMessage })
                      return
                    }
                    try {
                      navigate(`/courses/${courseId}/modules/${moduleId}/quiz`)
                    } catch (err) {
                      if (err?.status === 403) {
                        showErrorToast({ 
                          message: err?.message || tObj.courseReader.mustCompleteAllBlocks()
                        })
                      }
                    }
                  }}
                  disabled={!canAccessQuiz && !isQuizPassed}
                  className={`w-full h-9 text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                    canAccessQuiz || isQuizPassed
                      ? 'bg-[#203a8a] hover:bg-[#E8C547] hover:text-[#203a8a] text-white'
                      : 'bg-[#D5DAE3] text-[#9CA3AF] cursor-not-allowed'
                  }`}
                  size="sm"
                  title={!canAccessQuiz && !isQuizPassed 
                    ? (!allPreviousModulesCompleted 
                        ? tObj.courseReader.completePreviousModules()
                        : tObj.courseReader.completeAllBlocks())
                    : undefined}
                >
                  <FileQuestion className="w-3 h-3 mr-1" />
                  {isQuizPassed ? tObj.courseReader.viewQuiz() : canAccessQuiz ? tObj.courseReader.takeQuiz() : tObj.courseReader.quizLocked()}
                </Button>
                {!canAccessQuiz && !isQuizPassed && (
                  <p className="text-[10px] text-[#6B7280] mt-2 text-center leading-relaxed">
                    {!allPreviousModulesCompleted 
                      ? tObj.courseReader.completePreviousModules()
                      : !allLessonsCompleted
                      ? tObj.courseReader.completeAllLessons?.() || 'Vous devez compléter toutes les chapitres du module'
                      : tObj.courseReader.completeAllBlocks()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>

          {/* Main content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3">
            {/* Header éditorial */}
            <div className="bg-white border border-[#D5DAE3] p-4 sm:p-5 mb-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <Link
                    to={`/courses/${courseId}`}
                    className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7280] hover:text-[#203a8a] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                    <span className="hidden sm:inline">{tObj.courseReader.backToCourse()}</span>
                    <span className="sm:hidden">Retour</span>
                  </Link>
                  {currentLesson?.title && (
                    <>
                      <span className="hidden sm:block h-4 w-px bg-[#D5DAE3]"></span>
                      <div className="hidden sm:flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-[#E8C547]" />
                        <span className="font-display text-sm text-[#203a8a] truncate max-w-xs">{currentLesson.title}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280] font-semibold">
                    {blocks.length} {blocks.length > 1 ? 'blocs' : 'bloc'}
                  </span>
                </div>
              </div>
            </div>

            {/* Content - All blocks concatenated */}
            <motion.div
              key={targetLessonId}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="p-6 sm:p-8 bg-white border border-[#D5DAE3]"
            >
              {renderAllBlocks()}
            </motion.div>

            {/* Navigation éditoriale - Between Lessons */}
            <div className="mt-4 bg-white border border-[#D5DAE3] p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
                <Button
                  variant="outline"
                  onClick={goToPreviousLesson}
                  disabled={(!previousLesson && !previousModule) || isNavigating}
                  className="group inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold border border-[#D5DAE3] text-[#6B7280] hover:border-[#203a8a] hover:text-[#203a8a] hover:bg-white disabled:opacity-40"
                >
                  {isNavigating ? (
                    <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                  ) : (
                    <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                  <span className="hidden sm:inline">{previousLesson ? 'chapitre précédente' : previousModule ? 'Module précédent' : tObj.courseReader.previous()}</span>
                  <span className="sm:hidden">Précédent</span>
                </Button>

                <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#6B7280] font-semibold">
                  {currentLesson && (
                    <span>
                      <span className="font-display text-base text-[#203a8a] normal-case tracking-normal">{String(currentLessonIndex + 1).padStart(2, '0')}</span>
                      <span className="mx-1">/</span>
                      <span>{String(currentModule?.lessons?.length || 0).padStart(2, '0')}</span>
                    </span>
                  )}
                </div>

                <Button
                  onClick={goToNextLesson}
                  disabled={(!nextLesson && !isLastLesson && !moduleQuiz && !nextModule) || isNavigating}
                  className="group inline-flex items-center justify-center gap-2 bg-[#203a8a] hover:bg-[#E8C547] hover:text-[#203a8a] text-white disabled:opacity-50 text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors"
                >
                  {isNavigating ? (
                    <>
                      <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                      <span className="hidden sm:inline">{tObj.courseReader.loadingNav()}</span>
                      <span className="sm:hidden">Chargement...</span>
                    </>
                  ) : isLastLesson ? (
                    moduleQuiz ? (
                      <>
                        <span className="hidden sm:inline">Passer au quiz</span>
                        <span className="sm:hidden">Quiz</span>
                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      </>
                    ) : nextModule ? (
                      <>
                        <span className="hidden sm:inline">Module suivant</span>
                        <span className="sm:hidden">Suivant</span>
                        <SkipForward className="w-3 h-3 sm:w-4 sm:h-4" />
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">{tObj.courseReader.finish()}</span>
                        <span className="sm:hidden">Terminer</span>
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      </>
                    )
                  ) : nextLesson ? (
                    <>
                      <span className="hidden sm:inline">chapitre suivante</span>
                      <span className="sm:hidden">Suivant</span>
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </>
                  ) : (
                    <>
                      {tObj.courseReader.next()}
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Overlay for mobile sidebar (inchangé) */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </div>
      </div>

      {/* Module Lock Modal */}
      <ModuleLockModal
        isOpen={lockModalOpen}
        onClose={() => {
          setLockModalOpen(false)
          setModuleAccessError(null)
        }}
        lockedModule={currentModule ? { ...currentModule, courseId } : null}
        previousModule={accessDetails?.previousModuleId ? { id: accessDetails.previousModuleId, title: accessDetails.previousModuleTitle } : null}
        previousLesson={accessDetails?.previousLessonId ? { id: accessDetails.previousLessonId, title: accessDetails.previousLessonTitle, moduleId: accessDetails.moduleId } : null}
      />
    </div>
  )
}